import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { Allergy } from "../types";
import { allergyFormSchema, AllergyVariables } from "../schemas";
import { useCreateAllergyMutation, useUpdateAllergyMutation } from "../api/mutations";
import { getQueryClient } from "~/providers/query.provider";
import { allergiesKeys } from "../api/queries/keys";

interface UseAllergyProps {
    allergy?: Allergy | null;
    onClose?: () => void;
    program: 'weight_loss' | 'urgent_care';
}

export const useAllergy = ({ allergy, onClose, program }: UseAllergyProps) => {
    const queryClient = getQueryClient();
    const createAllergyMutation = useCreateAllergyMutation();
    const updateAllergyMutation = useUpdateAllergyMutation();

    const form = useForm<AllergyVariables>({
        resolver: zodResolver(allergyFormSchema),
        defaultValues: {
            name: allergy?.name ?? "",
            severity: allergy?.severity ?? "",
            symptoms: allergy?.symptoms ?? "",
            program: program,
        },
    });

    const onSubmit = async (data: AllergyVariables) => {
        if (allergy) {
            updateAllergyMutation.mutate({
                id: allergy?.allergy_id ?? "",
                name: data?.name,
                severity: data?.severity,
                symptoms: data?.symptoms,
                program,
            }, 
            {
                onSuccess: () => {
                    toast.success("Allergy updated successfully");
                    onClose?.();
                    queryClient.invalidateQueries({ queryKey: allergiesKeys.allergies(program) });
                },
                onError: () => {
                    toast.error("Failed to update allergy");
                }
            });
        } else {
            createAllergyMutation.mutate({
                name: data?.name,
                severity: data?.severity,
                symptoms: data?.symptoms,
                program,
            }, {
                onSuccess: () => {
                    toast.success("Allergy added successfully");
                    onClose?.();
                    queryClient.invalidateQueries({ queryKey: allergiesKeys.allergies(program) });
                },
                onError: () => {
                    toast.error("Failed to added allergy");
                }
            });
        }
    };

    return {
        form,
        onSubmit,
        isLoading: createAllergyMutation.isPending || updateAllergyMutation.isPending,
    };
};
